import dayjs from "dayjs"
import { ISelectedTicket } from "types/improved-booking"
import { ItimeSlot } from "types/ticket"

/**
 * Helper functions for handling tickets in the booking process
 */

/**
 * Determines if a timeslot is still available and active
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot is eligible for booking
 */
const isTimeslotEligible = (timeslot: ItimeSlot): boolean => {
    const currentDate = dayjs()
    const endDate = dayjs(timeslot.end_slot)

    // Check if the ticket is still available (not expired)
    if (!currentDate.isBefore(endDate)) {
        return false
    }

    // Check if the ticket is active
    return timeslot.status === "active"
}

/**
 * Checks if a timeslot has no tickets available
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot has no tickets
 */
export const isTimeslotWithoutTickets = (
    timeslot: ItimeSlot | any
): boolean => {
    return !timeslot.types || timeslot.types.length === 0
}

/**
 * Checks if all tickets in a timeslot are sold out (limit = 0 and no wait_list available)
 * @param timeslot The timeslot to check
 * @returns Whether all tickets in the timeslot are sold out
 */
export const isTimeslotSoldOut = (timeslot: ItimeSlot | any): boolean => {
    if (isTimeslotWithoutTickets(timeslot)) {
        return true
    }

    // Check if all ticket types are sold out AND have no wait_list available
    return timeslot.types.every((type: any) => {
        const isSoldOut = type.limit === 0 || type.limit < (type.min || 1);
        const hasWaitList = typeof type.wait_list === "number" && type.wait_list > 0;
        
        // A ticket is not sold out if it has wait_list available (even if limit is 0)
        return isSoldOut && !hasWaitList;
    })
}

/**
 * Checks if a single ticket type is sold out
 * @param ticketType The ticket type to check
 * @returns Whether the ticket type is sold out
 */
export const isTicketSoldOut = (ticketType: any): boolean => {
    const isSoldOut = ticketType.limit === 0 || ticketType.limit < (ticketType.min || 1);
    const hasWaitList = typeof ticketType.wait_list === "number" && ticketType.wait_list > 0;
    
    // A ticket is not sold out if it has wait_list available
    return isSoldOut && !hasWaitList;
}

/**
 * Checks if a timeslot should be disabled (sold out or no tickets)
 * @param timeslot The timeslot to check
 * @returns Whether the timeslot should be disabled
 */
export const isTimeslotDisabled = (timeslot: ItimeSlot | any): boolean => {
    // The only way to disable the timeslot is if the flag is explicitly false.
    if (timeslot.is_selling_time_open === false) {
        return true; // Disabled
    }

    // In all other cases (true or undefined), the timeslot is enabled.
    return false; // Enabled
};

/**
 * Gets all available timeslots, sorted by start time
 * @param time_slots Array of timeslots to filter
 * @returns Array of eligible timeslots, sorted by start time
 */
export const getAvailableTimeslots = (time_slots: ItimeSlot[]): ItimeSlot[] => {
    return time_slots
        ?.map((timeslot) =>
            isTimeslotEligible(timeslot) ? { ...timeslot } : null
        )
        ?.filter((timeslot): timeslot is ItimeSlot => timeslot !== null)
        ?.sort(
            (a, b) =>
                new Date(a.start_slot).getTime() -
                new Date(b.start_slot).getTime()
        )
}

/**
 * Filters tickets by a specific timeslot ID
 * @param timeslotId The timeslot ID to filter by
 * @param time_slots Array of timeslots to search
 * @returns Array of ticket types for the specified timeslot
 */
export const filterTicketsByTimeslotId = (
    timeslotId: string,
    time_slots: ItimeSlot[]
): ItimeSlot["types"] => {
    const timeslot = time_slots?.find((slot) => slot._id === timeslotId)
    return timeslot?.types || []
}

/**
 * Determines if the current ticket count qualifies for a promotion
 * @param ticketType The selected ticket type with promotion information
 * @param currentTicketCount The current count of tickets
 * @returns Whether the current count qualifies for a promotion
 */
export const isCountCurrentlyQualifiedForPromotion = (
    ticketType: ISelectedTicket,
    currentTicketCount: number
): boolean => {
    // If no promotion exists, return false
    if (!ticketType.promotion) {
        return false
    }

    const promotionStartCount = ticketType.promotion?.buy_qty || 0
    const promotionEndCount =
        promotionStartCount + (ticketType.promotion?.get_qty || 0)

    // Check if count is within the promotion range and we have at least one complete promotion set
    if (
        currentTicketCount >= promotionStartCount &&
        currentTicketCount < promotionEndCount
    ) {
        const fullPromotionSets = Math.floor(
            currentTicketCount / promotionStartCount
        )
        return fullPromotionSets > 0
    }

    return false
}

const buildPromotionKey = (typeId: string, timeslotId: string) =>
    `${typeId}::${timeslotId}`

type PromotionDetails = {
    _id?: string
    buy_qty?: number
    get_qty?: number
    status?: string
    discount_type?: number
}

type PromotionAggregation = {
    count: number
    freeCount: number
    promotion?: PromotionDetails
}

const isPromotionActive = (status?: string) =>
    status === undefined || status?.toLowerCase() === "active"

const hasReachedBuyThreshold = (count: number, buyQty?: number) => {
    if (!buyQty || buyQty <= 0) {
        return count > 0
    }
    return count >= buyQty
}

const hasCompletedBuyXGetY = (
    aggregation: PromotionAggregation,
    buyQty?: number,
    getQty?: number
) => {
    const requiredBuy = buyQty ?? 0
    const requiredFree = getQty ?? 0

    if (requiredBuy <= 0 && requiredFree <= 0) {
        return aggregation.freeCount > 0
    }

    const requiredTotal = requiredBuy + requiredFree
    if (requiredTotal <= 0) {
        return aggregation.freeCount > 0
    }

    if (aggregation.count < requiredTotal) {
        return false
    }

    if (requiredFree <= 0) {
        return true
    }

    return aggregation.freeCount >= requiredFree
}

/**
 * Builds a lookup map for promotions that have satisfied their eligibility criteria.
 * Returns a map keyed by `type_id::timeslot_id` containing the promotion ID when applicable.
 */
type PromotionComparableTicket = {
    type_id: string
    timeslot_id: string
    promotion?: PromotionDetails
    is_promotion_free?: boolean
}

export const buildPromotionEligibilityMap = <T extends PromotionComparableTicket>(
    tickets: T[]
): Map<string, string> => {
    const aggregation = new Map<string, PromotionAggregation>()

    tickets.forEach((ticket) => {
        const key = buildPromotionKey(ticket.type_id, ticket.timeslot_id)
        const current = aggregation.get(key) ?? {
            count: 0,
            freeCount: 0,
            promotion: undefined,
        }

        aggregation.set(key, {
            count: current.count + 1,
            freeCount: current.freeCount + (ticket.is_promotion_free ? 1 : 0),
            promotion: current.promotion ?? ticket.promotion,
        })
    })

    const result = new Map<string, string>()

    aggregation.forEach((value, key) => {
        const promotion = value.promotion

        if (!promotion?._id || !isPromotionActive(promotion.status)) {
            return
        }

        if (promotion.discount_type === 3) {
            if (hasCompletedBuyXGetY(value, promotion.buy_qty, promotion.get_qty)) {
                result.set(key, promotion._id)
            }
            return
        }

        if (hasReachedBuyThreshold(value.count, promotion.buy_qty)) {
            result.set(key, promotion._id)
        }
    })

    return result
}

export const getPromotionKey = (typeId: string, timeslotId: string) =>
    buildPromotionKey(typeId, timeslotId)
